import React, { useEffect, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'

/**
 * OAuth 回调页面 - 重定向组件
 *
 * 安卓 / 开发模式下，Casdoor 重定向到 /callback?code=...&state=...
 * 本组件检测到参数后重定向到根路径 /，由 useOAuthUrlCallback 统一处理。
 *
 * 这样做的好处：
 * 1. 回调处理逻辑只有一个入口（useOAuthUrlCallback），避免竞态
 * 2. 桌面端（tauri://localhost/）和安卓端（/callback）走同一套处理流程
 * 3. 简化维护，减少重复代码
 */
export const CallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const hasRedirected = useRef(false)

  useEffect(() => {
    if (hasRedirected.current) return

    const code = searchParams.get('code')
    const state = searchParams.get('state')
    const errorParam = searchParams.get('error')

    if (errorParam) {
      console.error('[CallbackPage] Casdoor 返回错误:', errorParam)
      hasRedirected.current = true
      // 导航到登录页，附带错误信息
      navigate('/login?error=' + encodeURIComponent('认证失败: ' + errorParam), { replace: true })
      return
    }

    if (code && state) {
      console.log('[CallbackPage] 检测到回调参数，重定向到根路径统一处理')
      hasRedirected.current = true
      // 重定向到根路径，保留 code 和 state 参数
      // useOAuthUrlCallback 会在根路径检测并处理这些参数
      navigate(`/?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`, { replace: true })
    }
  }, [searchParams, navigate])

  // 加载中状态（重定向前短暂显示）
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">正在登录...</h1>
        <p className="text-gray-600">请稍候，正在完成认证</p>
      </div>
    </div>
  )
}